import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../services/apiService';
import AddContributionModal from '../components/modals/AddContributionModal';
import ContributionDetailsModal from '../components/modals/ContributionDetailsModal';
import EditContributionModal from '../components/modals/EditContributionModal';
import About from './About';
import { 

  DollarSign, 
  ArrowLeft,
  Search,
  Download,
  Plus,
  Calendar,
  TrendingUp,
  TrendingDown,
  Edit,
  Trash2,
  Eye,
  UserCheck,
  Target,
  Banknote,
  X,
  XCircle,AArrowDown,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';

const ZoneContributionsPageNew = () => {
  const { user, isAuthenticated, isReadOnly } = useAuth();
  const navigate = useNavigate();
  
  // États de base
  const [contributions, setContributions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalContributions, setTotalContributions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    month: '',
    year: '', // Pas d'année par défaut pour voir toutes les données
    status: '',
    fraternity: ''
  });
  
  // Données de référence
  const [fraternities, setFraternities] = useState([]);
  const [globalStats, setGlobalStats] = useState({});
  
  // Pagination et tri
  const [itemsPerPage] = useState(15);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // États des modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [isAddingContribution, setIsAddingContribution] = useState(false);
  
  // Données supplémentaires pour les modales
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
/**
 * 
 */
  // Fonction pour formater les montants
  const formatAmount = useCallback((amount) => {
    if (!amount || amount === 0) return '0';
    const num = parseFloat(amount);
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace('.0', '') + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1).replace('.0', '') + 'K';
    }
    return num.toLocaleString();
  }, []);
  
  // Fonction pour convertir le numéro de mois en nom
  const getMonthName = useCallback((monthNumber) => {
    const monthNames = {
      1: 'Janvier', 2: 'Février', 3: 'Mars', 4: 'Avril', 
      5: 'Mai', 6: 'Juin', 7: 'Juillet', 8: 'Août', 
      9: 'Septembre', 10: 'Octobre', 11: 'Novembre', 12: 'Décembre'
    };
    return monthNames[monthNumber] || monthNumber;
  }, []);

  // Fonction pour obtenir l'icône et le style du statut
  const getStatusDisplay = useCallback((status) => {
    switch (status) {
      case 'paid':
        return {
          icon: <CheckCircle className="h-3 w-3 mr-1" />,
          text: 'Payé',
          style: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'pending':
        return {
          icon: <Clock className="h-3 w-3 mr-1" />,
          text: 'En attente',
          style: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'overdue':
        return {
          icon: <AlertTriangle className="h-3 w-3 mr-1" />,
          text: 'En retard',
          style: 'bg-red-100 text-red-800 border-red-200'
        };
      default:
        return {
          icon: <XCircle className="h-3 w-3 mr-1" />,
          text: 'Inconnu',
          style: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  }, []);

  // Charger les données initiales
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!user?.zone) {
      navigate('/dashboard');
      return;
    }

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, navigate]);

  // Recharger les contributions quand les filtres ou la page changent (SANS searchTerm)
  useEffect(() => {
    if (isAuthenticated && user?.zone) {
      console.log('Rechargement dû aux changements de filtres/page:', {
        filters,
        currentPage,
        sortBy,
        sortOrder
      });
      loadContributions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, sortBy, sortOrder, isAuthenticated, user]);

  // Debounce séparé pour la recherche avec reset de page
  useEffect(() => {
    if (!isAuthenticated || !user?.zone) return;
    
    // Si le terme est vide, pas besoin de debounce, charger immédiatement
    if (!searchTerm || searchTerm.trim() === '') {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadContributions();
      }
      return;
    }
    
    // Debounce seulement pour les recherches avec du contenu
    const timer = setTimeout(() => {
      console.log('Recherche déclenchée pour:', searchTerm.trim());
      // Toujours remettre à la page 1 lors d'une nouvelle recherche
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        // Si on est déjà sur la page 1, charger directement
        loadContributions();
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isAuthenticated, user]);

  // Réinitialiser la page courante quand les filtres ou la recherche changent (pour le filtrage côté client)
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  const loadInitialData = async () => {
    await Promise.all([
      loadGlobalStats(),
      loadFraternities(),
      loadContributions(),
      loadMembers()
    ]);
  };

  const loadGlobalStats = async () => {
    try {
      console.log('🔍 Chargement des statistiques globales...');
      const response = await apiService.get('/zone-dashboard/stats/global');
      console.log('✅ Stats globales reçues:', response);
      
      if (response && typeof response === 'object') {
        setGlobalStats(response);
        console.log('✅ Statistiques chargées avec succès');
      } else {
        console.error('❌ Réponse invalide pour les stats:', response);
        setGlobalStats({});
        setError('Format de réponse invalide pour les statistiques');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des stats globales:', error);
      setGlobalStats({});
      
      // Ne pas afficher d'erreur pour les stats globales si c'est juste un problème de connexion
      if (error.message !== 'Failed to fetch' && error.name !== 'TypeError') {
        setError(`Erreur lors du chargement des statistiques: ${error.message}`);
      }
    }
  };

  const loadFraternities = async () => {
    try {
      const response = await apiService.get('/zone-dashboard');
      console.log('Fraternités reçues:', response);
      // La réponse a la structure { fraternities: [...], zone: "...", user_role: "..." }
      setFraternities(response.fraternities || []);
    } catch (error) {
      console.error('Erreur lors du chargement des fraternités:', error);
      setFraternities([]);
      
      // Ne pas afficher d'erreur pour les fraternités si c'est juste un problème de connexion
      if (error.message !== 'Failed to fetch' && error.name !== 'TypeError') {
        console.warn('Fraternités non disponibles, filtre désactivé');
      }
    }
  };

  const loadContributions = async () => {
    try {
      setIsLoading(true);
      
      const params = {
        page: currentPage,
        per_page: itemsPerPage, // Force 15 éléments par page
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      // Ajouter les filtres seulement s'ils ont des valeurs
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          params[key] = value;
        }
      });
      
      // Ajouter la recherche seulement si elle a une valeur
      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm.trim();
      }

      console.log('Chargement contributions avec params:', params);
      const response = await apiService.get('/zone-dashboard/contributions', { params });
      console.log('Contributions reçues:', response);
      
      // Gérer les données paginées - s'assurer que la structure est correcte
      if (response && typeof response === 'object') {
        // Si c'est une réponse paginée Laravel standard
        if (response.data && Array.isArray(response.data)) {
          setContributions(response.data);
          setTotalContributions(response.total || 0);
          // Recalculer le nombre total de pages basé sur per_page côté client
          const calculatedPages = Math.ceil((response.total || 0) / itemsPerPage);
          setTotalPages(calculatedPages);
        } else if (Array.isArray(response)) {
          // Si la réponse est directement un tableau
          setContributions(response);
          setTotalContributions(response.length);
          setTotalPages(Math.ceil(response.length / itemsPerPage));
        } else {
          console.warn('Structure de réponse inattendue:', response);
          setContributions([]);
          setTotalContributions(0);
          setTotalPages(1);
        }
      } else {
        setContributions([]);
        setTotalContributions(0);
        setTotalPages(1);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des contributions:', error);
      setContributions([]);
      setTotalContributions(0);
      setTotalPages(1);
      
      // Messages d'erreur plus spécifiques
      let errorMessage = 'Erreur inconnue';
      
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez que le serveur backend est démarré.';
      } else if (error.status === 401) {
        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
      } else if (error.status === 403) {
        errorMessage = 'Accès non autorisé pour cette zone.';
      } else if (error.status === 500) {
        errorMessage = 'Erreur serveur. Contactez l\'administrateur.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(`Erreur lors du chargement: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      console.log('🔍 Chargement des membres de la zone...');
      const response = await apiService.get('/zone-dashboard/members');
      console.log('✅ Membres reçus:', response);
      
      if (response && response.members) {
        setMembers(response.members);
        console.log(`✅ ${response.members.length} membres chargés pour la zone ${response.zone}`);
      } else {
        console.error('❌ Structure de réponse invalide:', response);
        setMembers([]);
        setError('Format de réponse invalide pour les membres');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des membres:', error);
      setMembers([]);
      setError(`Erreur lors du chargement des membres: ${error.message}`);
    }
  };

  // Fonctions de gestion des contributions
  const handleAddContribution = async (contributionData) => {
    try {
      console.log('📝 Ajout d\'une nouvelle contribution:', contributionData);
      setIsAddingContribution(true);
      setError('');
      
      const response = await apiService.post('/zone-dashboard/contributions', contributionData);
      console.log('✅ Réponse ajout contribution:', response);
      
      if (response && (response.success || response.contribution)) {
        console.log('✅ Contribution ajoutée avec succès');
        setShowAddModal(false);
        await Promise.all([
          loadContributions(),
          loadGlobalStats()
        ]);
      } else {
        setError(response?.message || 'Erreur lors de l\'ajout de la contribution');
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de la contribution:', error);
      if (error.status === 422 && error.validationErrors) {
        const errorMessages = Object.values(error.validationErrors).flat().join(', ');
        setError(`Erreur de validation: ${errorMessages}`);
      } else {
        setError(`Erreur lors de l'ajout: ${error.message}`);
      }
    } finally {
      setIsAddingContribution(false);
    }
  };

  const handleExportContributions = async () => {
    try {
      console.log('📊 Exportation des contributions...');
      const params = {
        export: 'excel',
        ...filters,
        search: searchTerm
      };
      
      const response = await apiService.get('/zone-dashboard/contributions/export', { 
        params,
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contributions_zone_${user.zone}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Export terminé avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de l\'export:', error);
      
      // Fallback: Export simple en CSV
      try {
        console.log('🔄 Tentative d\'export CSV en fallback...');
        exportToCSV();
      } catch (csvError) {
        setError('Erreur lors de l\'exportation des données');
      }
    }
  };

  const exportToCSV = async () => {
    try {
      console.log('📊 Export CSV en cours...');
      // Charger toutes les contributions pour l'export
      const exportParams = {
        per_page: 1000, // Grande limite pour l'export
        ...filters
      };
      
      if (searchTerm) exportParams.search = searchTerm;
      
      const response = await apiService.get('/zone-dashboard/contributions', { params: exportParams });
      const allContributions = response.data || [];
      
      console.log(`📋 Export de ${allContributions.length} contributions`);
      
      const csvData = [
        ['Nom', 'Prénom', 'Fraternité', 'Montant', 'Période', 'Statut', 'Date de Création'],
        ...allContributions.map(contrib => [
          contrib.user?.last_name || '',
          contrib.user?.first_name || '',
          contrib.user?.fraternity || '',
          contrib.amount || 0,
          `${getMonthName(contrib.period_month)} ${contrib.period_year}`,
          contrib.status === 'paid' ? 'Payé' : contrib.status === 'pending' ? 'En attente' : 'En retard',
          new Date(contrib.created_at).toLocaleDateString()
        ])
      ];
      
      const csvContent = csvData.map(row => row.map(field => `"${field}"`).join(';')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `contributions_zone_${user.zone}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      console.log('✅ Export CSV terminé');
    } catch (error) {
      console.error('❌ Erreur export CSV:', error);
      setError('Erreur lors de l\'export CSV');
    }
  };

  const resetFilters = () => {
    console.log('Réinitialisation des filtres');
    
    // Réinitialiser tous les filtres
    setFilters({
      month: '',
      year: '',  // Enlever l'année par défaut pour voir toutes les données
      status: '',
      fraternity: ''
    });
    
    // Réinitialiser la recherche
    setSearchTerm('');
    
    // Remettre à la première page
    setCurrentPage(1);
    
    // Réinitialiser l'erreur s'il y en a une
    setError('');
    
    // Forcer le rechargement des données
    setTimeout(() => {
      loadContributions();
    }, 100);
  };

  const handleViewDetails = (contribution) => {
    setSelectedContribution(contribution);
    setShowDetailsModal(true);
  };

  const handleUpdateContribution = async (contributionId, updates) => {
    try {
      console.log('🔄 Mise à jour de la contribution:', contributionId, updates);
      setError('');
      
      const response = await apiService.put(`/zone-dashboard/contributions/${contributionId}`, updates);
      console.log('✅ Réponse mise à jour:', response);
      
      if (response && (response.success || response.contribution)) {
        console.log('✅ Contribution mise à jour avec succès');
        setShowEditModal(false);
        await Promise.all([
          loadContributions(),
          loadGlobalStats()
        ]);
      } else {
        setError(response?.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      setError(`Erreur lors de la mise à jour: ${error.message}`);
    }
  };

  const handleDeleteContribution = async (contributionId) => {
    try {
      console.log('🗑️ Suppression de la contribution:', contributionId);
      setError('');
      
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette contribution ?')) {
        return;
      }
      
      const response = await apiService.delete(`/zone-dashboard/contributions/${contributionId}`);
      console.log('✅ Réponse suppression:', response);
      
      if (response && (response.success !== false)) {
        console.log('✅ Contribution supprimée avec succès');
        await Promise.all([
          loadContributions(),
          loadGlobalStats()
        ]);
      } else {
        setError(response?.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      setError(`Erreur lors de la suppression: ${error.message}`);
    }
  };

  // Filtrage côté client pour une meilleure responsivité (inspiré de ContributionsPage)
  const filteredContributions = useMemo(() => {
    return contributions.filter(contribution => {
      // Filtre par année
      if (filters.year && contribution.period_year?.toString() !== filters.year) {
        return false;
      }
      
      // Filtre par mois
      if (filters.month && contribution.period_month?.toString() !== filters.month) {
        return false;
      }
      
      // Filtre par statut
      if (filters.status && contribution.status !== filters.status) {
        return false;
      }
      
      // Filtre par fraternité
      if (filters.fraternity && contribution.user?.fraternity !== filters.fraternity) {
        return false;
      }
      
      // Filtre par recherche (nom, email, fraternité)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const userName = (contribution.user_name || contribution.user?.name || '').toLowerCase();
        const userEmail = (contribution.user?.email || '').toLowerCase();
        const userFraternity = (contribution.user?.fraternity || '').toLowerCase();
        
        if (!userName.includes(searchLower) && 
            !userEmail.includes(searchLower) && 
            !userFraternity.includes(searchLower)) {
          return false;
        }
      }
      
      return true;
    }).sort((a, b) => {
      // Tri par date de création du plus récent au plus ancien
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });
  }, [contributions, filters, searchTerm]);

  // Pagination côté client
  const totalFilteredItems = filteredContributions.length;
  const totalFilteredPages = Math.ceil(totalFilteredItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContributions = filteredContributions.slice(startIndex, endIndex);

  const filteredStats = useMemo(() => {
    const totalAmount = filteredContributions.reduce((sum, contrib) => sum + (parseFloat(contrib.amount) || 0), 0);
    const paidAmount = filteredContributions.filter(c => c.status === 'paid').reduce((sum, contrib) => sum + (parseFloat(contrib.amount) || 0), 0);
    const pendingAmount = filteredContributions.filter(c => c.status === 'pending').reduce((sum, contrib) => sum + (parseFloat(contrib.amount) || 0), 0);
    const overdueAmount = filteredContributions.filter(c => c.status === 'overdue').reduce((sum, contrib) => sum + (parseFloat(contrib.amount) || 0), 0);
    
    return {
      totalAmount,
      paidAmount,
      pendingAmount: pendingAmount + overdueAmount,
      count: filteredContributions.length, // Utiliser le nombre filtré côté client
      paidCount: filteredContributions.filter(c => c.status === 'paid').length
    };
  }, [filteredContributions]);

  // Composants utilitaires
  const StatsCard = ({ title, value, subtitle, icon: Icon, color = "blue", trend, trendValue }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600 mb-2`}>{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const years = Array.from({length: 2030 - 2020 + 1}, (_, i) => 2020 + i);

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  if (!isAuthenticated || !user?.zone) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 space-y-3 sm:space-y-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <Link
                to="/zone-dashboard"
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                <span className="text-sm sm:text-base">Retour au Dashboard</span>
              </Link>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
                  <span className="sm:hidden">Cotisations - Zone {user.zone}</span>
                  <span className="hidden sm:inline">Gestion des Cotisations - Zone {user.zone}</span>
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">
                  Suivre et gérer les cotisations des membres de votre zone
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-end">
              {!isReadOnly && (
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nouvelle Cotisation</span>
                <span className="sm:hidden">Nouvelle</span>
              </button>
              )}
              
              <button
                onClick={handleExportContributions}
                className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition-colors text-sm"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Exporter</span>
                <span className="sm:hidden">Export</span>
              </button>
              
              {/* Bouton de débogage temporaire */}
              {/* <button
                onClick={() => {
                  console.log('=== DEBUG INFO ===' );
                  console.log('Contributions reçues du backend:', contributions.length);
                  console.log('Contributions à afficher (max 15):', Math.min(contributions.length, itemsPerPage));
                  console.log('Total contributions (global):', totalContributions);
                  console.log('Page actuelle:', currentPage);
                  console.log('Total pages:', totalPages);
                  console.log('Items per page:', itemsPerPage);
                  console.log('Filtres actifs:', Object.entries(filters).filter(([k,v]) => v).map(([k,v]) => `${k}: ${v}`).join(', ') || 'Aucun');
                  console.log('Terme recherche:', `"${searchTerm}"`, searchTerm.length > 0 ? `(${searchTerm.length} caractères)` : '(vide)');
                  console.log('Dernier paramètres envoyés:', {
                    page: currentPage,
                    per_page: itemsPerPage,
                    search: searchTerm.trim() || undefined,
                    ...Object.fromEntries(Object.entries(filters).filter(([k,v]) => v))
                  });
                  console.log('=================');
                }}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-2 rounded-lg text-xs"
              >
                Debug
              </button> */}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                <div className="mt-1 text-sm text-red-700">
                  {error}
                </div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError('')}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Collecté (Tout temps)"
            value={formatAmount(globalStats?.totalCollectedAllTime || 0)}
            subtitle="F CFA depuis la création"
            icon={Banknote}
            color="green"
            trend="up"
            trendValue="+12% ce mois"
          />
          
          <StatsCard
            title="Collecté cette année"
            value={formatAmount(globalStats?.currentYearTotal || 0)}
            subtitle={`F CFA en ${new Date().getFullYear()}`}
            icon={Calendar}
            color="blue"
            trend="up"
            trendValue="+8% vs 2024"
          />
          
          <StatsCard
            title="Taux de collecte"
            value={globalStats?.collectionRate ? `${globalStats.collectionRate}%` : '0%'}
            subtitle={`${formatAmount(globalStats?.monthlyCollected || 0)}/${formatAmount(globalStats?.monthlyTarget || 0)} F ce mois`}
            icon={Target}
            color="purple"
          />
          
          <StatsCard
            title="Membres cotisants"
            value={globalStats?.contributingMembers || 0}
            subtitle={`sur ${globalStats?.totalMembers || 0} membres`}
            icon={UserCheck}
            color="orange"
          />
        </div>

        {/* Barre de recherche et filtres */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
            {/* Recherche */}
            <div className="lg:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher
                {searchTerm && (
                  <span className="text-xs text-gray-500 ml-2">
                    ({totalContributions} résultat{totalContributions !== 1 ? 's' : ''})
                  </span>
                )}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom, prénom ou fraternité..."
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    console.log('Changement terme recherche:', value);
                    setSearchTerm(value);
                    // Le reset de page se fera automatiquement via l'useEffect de recherche
                  }}
                />
                {/* Bouton pour vider la recherche */}
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      console.log('Recherche vidée');
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Filtres */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Année
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.year}
                onChange={(e) => {
                  console.log('Changement filtre année:', e.target.value);
                  setFilters(prev => ({ ...prev, year: e.target.value }));
                  setCurrentPage(1);
                }}
              >
                <option value="">Toutes les années</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mois
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.month}
                onChange={(e) => {
                  console.log('Changement filtre mois:', e.target.value);
                  setFilters(prev => ({ ...prev, month: e.target.value }));
                  setCurrentPage(1);
                }}
              >
                <option value="">Tous les mois</option>
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fraternité
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.fraternity}
                onChange={(e) => {
                  console.log('Changement filtre fraternité:', e.target.value);
                  setFilters(prev => ({ ...prev, fraternity: e.target.value }));
                  setCurrentPage(1);
                }}
              >
                <option value="">Toutes les fraternités</option>
                {fraternities.map(fraternity => (
                  <option key={fraternity.name} value={fraternity.name}>
                    {fraternity.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.status}
                onChange={(e) => {
                  console.log('Changement filtre statut:', e.target.value);
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setCurrentPage(1);
                }}
              >
                <option value="">Tous les statuts</option>
                <option value="paid">Payé</option>
                <option value="pending">En attente</option>
                <option value="overdue">En retard</option>
              </select>
            </div>
          </div>

          {/* Résumé des filtres */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-gray-600">Total affiché:</span>
                <span className="font-semibold ml-2">{formatAmount(filteredStats.totalAmount)} FCFA</span>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <span className="text-gray-600">Montant payé:</span>
                <span className="font-semibold ml-2 text-green-600">{formatAmount(filteredStats.paidAmount)} F CFA</span>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <span className="text-gray-600">En attente:</span>
                <span className="font-semibold ml-2 text-yellow-600">{formatAmount(filteredStats.pendingAmount)} FCFA</span>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <span className="text-gray-600">Contributions:</span>
                <span className="font-semibold ml-2 text-blue-600">{filteredStats.count} ({filteredStats.paidCount} payées)</span>
              </div>
            </div>
          </div>
          
          {/* Boutons d'action */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-3 justify-end">
            <button
              onClick={resetFilters}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors"
            >
              <X className="h-4 w-4 inline-block mr-2" />
              Réinitialiser{searchTerm || Object.values(filters).some(f => f) ? ' tout' : ''}
            </button>
            <button
              onClick={handleExportContributions}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
            >
              <Download className="h-4 w-4 inline-block mr-2" />
              Exporter Excel
            </button>
          </div>
        </div>

        {/* Tableau des contributions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              <span className="sm:hidden">Contributions ({totalFilteredItems})</span>
              <span className="hidden sm:inline">
                Contributions ({totalFilteredItems} {Object.values(filters).some(f => f) || searchTerm ? 'filtrées sur ' + totalContributions + ' total' : 'au total'}, page {currentPage}/{totalFilteredPages})
              </span>
            </h2>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {sortOrder === 'asc' ? '↑' : '↓'} <span className="hidden sm:inline">Tri</span>
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des contributions...</p>
            </div>
          ) : paginatedContributions.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              {searchTerm ? (
                <>
                  <p className="text-gray-600 mb-2">Aucune contribution trouvée pour "{searchTerm}"</p>
                  <p className="text-sm text-gray-500">Essayez avec d'autres mots-clés ou modifiez vos filtres</p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-3 text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    Vider la recherche
                  </button>
                </>
              ) : Object.values(filters).some(f => f) ? (
                <>
                  <p className="text-gray-600 mb-2">Aucune contribution trouvée avec ces filtres</p>
                  <p className="text-sm text-gray-500">Essayez de modifier ou réinitialiser vos filtres</p>
                  <button
                    onClick={resetFilters}
                    className="mt-3 text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    Réinitialiser les filtres
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-2">Aucune contribution trouvée</p>
                  {!isReadOnly && (
                  <p className="text-sm text-gray-500">Ajoutez de nouvelles contributions pour commencer</p>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Vue desktop - tableau */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Membre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fraternité
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Période
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date de création
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedContributions.map((contribution) => (
                    <tr key={contribution.id} className="hover:bg-gray-50 text-justify">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                              {contribution.user?.first_name?.charAt(0)}{contribution.user?.last_name?.charAt(0)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {contribution.user?.first_name} {contribution.user?.last_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {contribution.user?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {contribution.user?.fraternity || 'Non assigné'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                          <span>{getMonthName(contribution.period_month)} {contribution.period_year}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          {/* <DollarSign className="h-4 w-4 mr-1 text-green-600" /> */}
                          <span title={`${parseFloat(contribution.amount || 0).toLocaleString()} F CFA`}>
                            {formatAmount(contribution.amount || 0)} F CFA
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const statusInfo = getStatusDisplay(contribution.status);
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusInfo.style}`}>
                              {statusInfo.icon}
                              {statusInfo.text}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(contribution.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewDetails(contribution)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50"
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {!isReadOnly && (
                          <button
                            onClick={() => {
                              setSelectedContribution(contribution);
                              setShowEditModal(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          )}
                          
                          {!isReadOnly && (
                          <button
                            onClick={() => handleDeleteContribution(contribution.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Vue mobile - cartes */}
            <div className="md:hidden space-y-4 p-4">
              {paginatedContributions.map((contribution) => (
                <div key={contribution.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  {/* Header de la carte */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                        {contribution.user?.first_name?.charAt(0)}{contribution.user?.last_name?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {contribution.user?.first_name} {contribution.user?.last_name}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">{contribution.user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={() => handleViewDetails(contribution)}
                        className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {!isReadOnly && (
                      <button
                        onClick={() => {
                          setSelectedContribution(contribution);
                          setShowEditModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-md hover:bg-indigo-50"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      )}
                      {!isReadOnly && (
                      <button
                        onClick={() => handleDeleteContribution(contribution.id)}
                        className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      )}
                    </div>
                  </div>

                  {/* Contenu de la carte */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Fraternité:</span>
                      <div className="mt-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {contribution.user?.fraternity || 'Non assigné'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Période:</span>
                      <div className="mt-1 flex items-center">
                        <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                        <span className="text-gray-900 text-xs">{getMonthName(contribution.period_month)} {contribution.period_year}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Montant:</span>
                      <div className="mt-1 font-medium text-gray-900">
                        {formatAmount(contribution.amount || 0)} F CFA
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Statut:</span>
                      <div className="mt-1">
                        {(() => {
                          const statusInfo = getStatusDisplay(contribution.status);
                          return (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusInfo.style}`}>
                              {statusInfo.icon}
                              {statusInfo.text}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Footer de la carte */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      Créé le {new Date(contribution.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}

          {/* Pagination améliorée */}
          <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalFilteredPages, currentPage + 1))}
                disabled={currentPage >= totalFilteredPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de{' '}
                  <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span>
                  {' '} à{' '}
                  <span className="font-medium">
                    {((currentPage - 1) * itemsPerPage) + Math.min(contributions.length, itemsPerPage)}
                  </span>
                  {' '}sur{' '}
                  <span className="font-medium">{totalContributions}</span>
                  {' '}contributions • Page {currentPage} sur {totalPages} • {Math.min(contributions.length, itemsPerPage)} affichés sur {itemsPerPage} max
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Précédent</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Pages directes */}
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + index;
                    if (pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Suivant</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modales */}
      <AddContributionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddContribution}
        members={members}
        isLoading={isAddingContribution}
      />
      
      <ContributionDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        contribution={selectedContribution}
      />
      
      <EditContributionModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedContribution(null);
        }}
        onSubmit={handleUpdateContribution}
        contribution={selectedContribution}
        isLoading={false}
      />
    </div>
  );
};

export default ZoneContributionsPageNew;